import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api";
import { hubApi, hubKeys } from "../hub/hubApi";
import { FkIcon, type FkIconName } from "./Icons";
import {
  SEARCH_DEBOUNCE_MS,
  flattenSearch,
  moveSelection,
  parseSearch,
  safeSearchPath,
  searchGroups,
  searchHint,
  type SearchGroupKey,
} from "./globalSearchModel";
import "./globalSearch.css";

const GROUP_ICON: Record<SearchGroupKey, FkIconName> = {
  campaigns: "send",
  guests: "user",
  promos: "gift",
  templates: "doc",
};

/** Значение с задержкой: запрос на сервер уходит, когда человек перестал печатать. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Глобальный поиск в шапке (пакет 8, п.12): кампании по названию, гость по телефону (от 4 цифр),
 * промокод, шаблон. Результаты — выпадающий список с переходом по клику и клавишами ↑ ↓ Enter,
 * Esc закрывает. Запрос уходит после паузы в 300 мс и только от 3 символов; сервер сам режет
 * выдачу областью пользователя и прячет телефоны без права на контакты. На телефоне поле
 * открывается кнопкой-лупой на всю ширину шапки.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const parsed = parseSearch(value);
  const debounced = useDebounced(parsed.query, SEARCH_DEBOUNCE_MS);
  const ready = parseSearch(debounced).ready;
  const query = useQuery({
    queryKey: hubKeys.search(debounced),
    queryFn: ({ signal }) => hubApi.search(debounced, signal),
    enabled: ready,
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
  });

  const groups = useMemo(() => (ready ? searchGroups(query.data) : []), [ready, query.data]);
  const items = useMemo(() => flattenSearch(groups), [groups]);
  const open = (focused || mobileOpen) && parsed.query !== "";

  // Переход закрывает список; новый ответ сбрасывает выбор.
  useEffect(() => {
    setFocused(false);
    setMobileOpen(false);
  }, [pathname, search]);
  useEffect(() => setActive(-1), [items]);

  // Клик вне поля закрывает выпадающий список.
  useEffect(() => {
    if (!focused && !mobileOpen) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setFocused(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [focused, mobileOpen]);

  const go = useCallback(
    (path: string) => {
      const safe = safeSearchPath(path);
      if (!safe) return;
      setFocused(false);
      setMobileOpen(false);
      inputRef.current?.blur();
      navigate(safe);
    },
    [navigate],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => moveSelection(current, event.key === "ArrowDown" ? 1 : -1, items.length));
    } else if (event.key === "Enter") {
      const target = items[active] ?? (items.length === 1 ? items[0] : undefined);
      if (target) {
        event.preventDefault();
        go(target.path);
      }
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setFocused(false);
      setMobileOpen(false);
      inputRef.current?.blur();
    }
  };

  const hint = searchHint(parsed);
  const busy = ready && (query.isFetching || debounced !== parsed.query);
  const rateLimited = query.error instanceof ApiError && query.error.status === 429;

  let body: ReactNode = null;
  if (open) {
    if (hint) body = <div className="fk-gsearch-note">{hint}</div>;
    else if (query.isError)
      body = (
        <div className="fk-gsearch-note" role="alert">
          {rateLimited ? "Поиск занят, подождите секунду." : "Не удалось выполнить поиск. Попробуйте ещё раз."}
        </div>
      );
    else if (!query.data && busy) body = <div className="fk-gsearch-note">Ищем…</div>;
    else if (query.data && groups.length === 0 && !busy)
      body = (
        <div className="fk-gsearch-note">
          Ничего не найдено.{" "}
          {parsed.phone && parsed.digitsLeft > 0 ? `Для поиска гостя введите не меньше 4 цифр номера.` : "Попробуйте другое слово или часть номера."}
        </div>
      );
    else if (groups.length > 0) {
      let index = -1;
      body = (
        <>
          {groups.map((group) => (
            <div key={group.key} role="group" aria-label={group.label}>
              <div className="fk-gsearch-group">{group.label}</div>
              {group.items.map((item) => {
                index += 1;
                const position = index;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="option"
                    id={`${listId}-${position}`}
                    aria-selected={position === active}
                    className={`fk-gsearch-item${position === active ? " is-active" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(position)}
                    onClick={() => go(item.path)}
                  >
                    <FkIcon name={GROUP_ICON[item.group]} size={15} className="fk-gsearch-icon" />
                    <span className="fk-gsearch-text">
                      <span className="fk-gsearch-title">{item.title}</span>
                      {item.subtitle && <span className="fk-gsearch-sub">{item.subtitle}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          {query.data?.partial && <div className="fk-gsearch-note fk-gsearch-foot">Часть результатов не успела загрузиться.</div>}
        </>
      );
    }
  }

  return (
    <div
      ref={rootRef}
      className={`fk-gsearch${mobileOpen ? " is-open" : ""}`}
      onBlur={(event) => {
        // Фокус ушёл из поля и списка (Tab) — выпадающий список закрываем.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <button
        type="button"
        className="fk-icon-btn fk-gsearch-toggle"
        aria-label="Поиск"
        aria-expanded={mobileOpen}
        onClick={() => {
          setMobileOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <FkIcon name="search" size={16} />
      </button>
      <div className="fk-gsearch-box">
        <FkIcon name="search" size={15} className="fk-gsearch-lens" />
        <input
          ref={inputRef}
          type="search"
          className="fk-gsearch-input"
          role="combobox"
          aria-label="Поиск по кампаниям, гостям, промокодам и шаблонам"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Поиск: кампания, телефон, промокод"
          value={value}
          maxLength={100}
          onChange={(event) => setValue(event.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={onKeyDown}
        />
        {open && (
          <div id={listId} role="listbox" aria-label="Результаты поиска" className="fk-gsearch-pop">
            {body}
          </div>
        )}
      </div>
    </div>
  );
}
